/**
 * Line pricing, item discounts and bill-discount allocation.
 *
 * Indian retail rule: a discount reduces the taxable value, so tax is computed on the
 * post-discount amount. Bill discounts are allocated across lines proportionally to the
 * post-item-discount value using the largest-remainder method, so the paise always sum
 * back exactly to the discount that was given.
 */

import type { BasisPoints, Milli, Paise } from './money.js';
import { applyRatio, assertMilli, assertPaise, multiply, roundHalfUp } from './money.js';
import type { TaxBucket, TaxComponents, TaxRate } from './tax.js';
import { computeTax, emptyBucket, mergeBucket } from './tax.js';

export type LineDiscount =
  | { type: 'none' }
  | { type: 'percent'; bps: BasisPoints }
  | { type: 'amount'; paise: Paise; perUnit?: boolean };

export interface PriceLineInput {
  lineId: string;
  productId: string;
  productName: string;
  batchId?: string | null;
  batchNumber?: string | null;
  qtyMilli: Milli;
  /** Selling rate per base unit. */
  ratePaise: Paise;
  /** MRP per base unit, used for the "selling above MRP" readiness check. */
  mrpPaise: Paise;
  discount: LineDiscount;
  rate: TaxRate;
  /** True when the shelf rate already contains GST (MRP-inclusive retail). */
  pricingInclusive: boolean;
  hsnCode?: string | null;
}

export interface PricedLine {
  lineId: string;
  productId: string;
  productName: string;
  batchId: string | null;
  batchNumber: string | null;
  hsnCode: string | null;
  qtyMilli: Milli;
  ratePaise: Paise;
  mrpPaise: Paise;
  /** rate x qty, before any discount */
  grossPaise: Paise;
  itemDiscountPaise: Paise;
  /** share of the bill discount allocated to this line */
  billDiscountPaise: Paise;
  /** (gross - itemDiscount - billDiscount): the value tax is charged on */
  taxablePaise: Paise;
  tax: TaxComponents;
  totalPaise: Paise;
  /** effective rate per unit after all discounts, in milli-paise per milli-unit */
  effectiveRateMilliPaise: number;
  rateId: string;
  /**
   * Set only by `removeTax` in `discount` mode: the part of `billDiscountPaise` that is the tax
   * taken off the bill, so screens can show it apart from a discount the cashier gave.
   */
  taxReliefPaise?: Paise;
}

export function computeItemDiscount(grossPaise: Paise, discount: LineDiscount, qtyMilli: Milli): Paise {
  switch (discount.type) {
    case 'none':
      return 0;
    case 'percent': {
      // Negative or >100% percentages are never a surcharge: clamp to 0..10000 bps.
      if (!Number.isFinite(discount.bps)) throw new Error(`Invalid discount ${discount.bps} bps`);
      const bps = Math.min(Math.max(0, Math.floor(discount.bps)), 10_000);
      if (bps === 0) return 0;
      const value = applyRatio(grossPaise, bps, 10_000);
      return Math.min(Math.max(0, value), Math.max(0, grossPaise));
    }
    case 'amount': {
      const value = discount.perUnit ? multiply(discount.paise, qtyMilli) : discount.paise;
      return Math.min(Math.max(0, value), Math.max(0, grossPaise));
    }
  }
}

export function priceLine(input: PriceLineInput, interState: boolean): PricedLine {
  assertMilli(input.qtyMilli, `${input.productName} quantity`);
  assertPaise(input.ratePaise, `${input.productName} rate`);
  assertPaise(input.mrpPaise, `${input.productName} MRP`);
  if (input.qtyMilli < 0) throw new Error(`${input.productName} quantity cannot be negative`);
  if (input.qtyMilli === 0) throw new Error(`${input.productName} quantity must be greater than zero`);
  if (input.ratePaise < 0) throw new Error(`${input.productName} rate cannot be negative`);
  if (input.mrpPaise < 0) throw new Error(`${input.productName} MRP cannot be negative`);
  if (!Number.isInteger(input.qtyMilli) || !Number.isInteger(input.ratePaise)) {
    throw new Error(`${input.productName} quantity and rate must be integers (paise / milli-units)`);
  }
  if (input.rate.gstBps < 0 || input.rate.gstBps > 2800 || input.rate.cessBps < 0 || input.rate.cessBps > 3000) {
    throw new Error(`Invalid tax rate for ${input.productName}`);
  }

  const grossPaise = multiply(input.ratePaise, input.qtyMilli);
  const itemDiscountPaise = computeItemDiscount(grossPaise, input.discount, input.qtyMilli);
  const afterItemDiscount = grossPaise - itemDiscountPaise;
  const tax = computeTax(afterItemDiscount, input.rate, {
    inclusive: input.pricingInclusive,
    interState,
  });

  return {
    lineId: input.lineId,
    productId: input.productId,
    productName: input.productName,
    batchId: input.batchId ?? null,
    batchNumber: input.batchNumber ?? null,
    hsnCode: input.hsnCode ?? null,
    qtyMilli: input.qtyMilli,
    ratePaise: input.ratePaise,
    mrpPaise: input.mrpPaise,
    grossPaise,
    itemDiscountPaise,
    billDiscountPaise: 0,
    // For inclusive pricing `afterItemDiscount` still contains the tax, so the taxable
    // value must come from the tax engine, not from the discounted gross.
    taxablePaise: tax.taxablePaise,
    tax,
    totalPaise: tax.totalPaise,
    effectiveRateMilliPaise:
      input.qtyMilli === 0 ? 0 : roundHalfUp((tax.taxablePaise * 1000) / input.qtyMilli),
    rateId: input.rate.id,
  };
}

export type BillDiscount =
  | { type: 'none' }
  | { type: 'percent'; bps: BasisPoints }
  | { type: 'amount'; paise: Paise };

/**
 * Largest-remainder proportional allocation. The returned amounts always sum to exactly
 * `total` — this is what keeps a receipt from being off by a single paise.
 */
export function allocateProportionally(total: Paise, weights: number[]): Paise[] {
  const sum = weights.reduce((accumulator, weight) => accumulator + weight, 0);
  if (sum <= 0 || total === 0) return weights.map(() => 0);

  const exact = weights.map((weight) => (weight / sum) * total);
  const floors = exact.map((value) => Math.floor(value));
  let remainder = total - floors.reduce((accumulator, value) => accumulator + value, 0);

  const order = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);

  const result = [...floors];
  for (const entry of order) {
    if (remainder <= 0) break;
    result[entry.index] = (result[entry.index] ?? 0) + 1;
    remainder -= 1;
  }
  return result;
}

/**
 * Price every line then allocate the bill discount across them.
 * The caller keeps ownership of the authoritative TaxRate objects so a line can be
 * re-priced after its share of the bill discount is known.
 */
export function priceLines(
  inputs: PriceLineInput[],
  billDiscount: BillDiscount,
  interState: boolean,
): PricedLine[] {
  const priced = inputs.map((input) => priceLine(input, interState));
  return applyBillDiscount(priced, billDiscount, interState, (line) => {
    const source = inputs.find((candidate) => candidate.lineId === line.lineId);
    if (!source) throw new Error(`Unknown rate for line ${line.lineId}: refusing to price at 0% GST`);
    return source.rate;
  });
}

export function applyBillDiscount(
  lines: PricedLine[],
  billDiscount: BillDiscount,
  interState: boolean,
  resolveRate: (line: PricedLine) => TaxRate,
): PricedLine[] {
  const baseValues = lines.map((line) => line.taxablePaise);
  const baseTotal = baseValues.reduce((accumulator, value) => accumulator + value, 0);
  if (baseTotal <= 0) return lines;

  let discountTotal: Paise;
  switch (billDiscount.type) {
    case 'none':
      discountTotal = 0;
      break;
    case 'percent': {
      if (!Number.isFinite(billDiscount.bps)) throw new Error(`Invalid bill discount ${billDiscount.bps} bps`);
      const bps = Math.min(Math.max(0, Math.floor(billDiscount.bps)), 10_000);
      discountTotal = bps === 0 ? 0 : Math.min(applyRatio(baseTotal, bps, 10_000), baseTotal);
      break;
    }
    case 'amount':
      discountTotal = Math.min(Math.max(0, billDiscount.paise), baseTotal);
      break;
  }
  if (discountTotal === 0) return lines;

  const allocations = allocateProportionally(discountTotal, baseValues);
  return lines.map((line, index) => {
    const share = allocations[index] ?? 0;
    const rate = resolveRate(line);
    const taxablePaise = line.taxablePaise - share;
    const tax = computeTax(taxablePaise, rate, { inclusive: false, interState });
    return {
      ...line,
      billDiscountPaise: share,
      taxablePaise,
      tax,
      totalPaise: tax.totalPaise,
      effectiveRateMilliPaise:
        line.qtyMilli === 0 ? 0 : roundHalfUp((taxablePaise * 1000) / line.qtyMilli),
    };
  });
}

export function taxBucketsFor(lines: PricedLine[], ratesById: Map<string, TaxRate>): Map<string, TaxBucket> {
  const buckets = new Map<string, TaxBucket>();
  for (const line of lines) {
    const rate = ratesById.get(line.rateId);
    if (!rate) throw new Error(`Unknown tax rate ${line.rateId}: refusing to bucket at 0% GST`);
    const bucket = buckets.get(line.rateId) ?? emptyBucket(rate);
    buckets.set(line.rateId, mergeBucket(bucket, line.tax));
  }
  return buckets;
}

export function warningsForLine(line: PriceLineInput): string[] {
  const warnings: string[] = [];
  if (line.ratePaise > 0 && line.mrpPaise > 0 && line.ratePaise > line.mrpPaise) {
    warnings.push('SELLING_ABOVE_MRP');
  }
  return warnings;
}
